<script setup>
import * as yup from 'yup';
import { ref, computed } from 'vue';
import { Field, useForm } from 'vee-validate';
import { __, requestSimActivation, convertArrayToObject, handleNumberFieldPaste } from '@/utils';
import Modal from "@components/modal.vue";
import Hint from '@components/hint.vue';
import Loader from '@components/loader.vue';
import ModalOtp from './parts/modal-otp.vue';
import FraudModal from "@components/FraudModal.vue";

const props = defineProps({
    setPhone: Function,
    setResponse: Function,
});

const errorStrings = [
    "incorrect_sim",
    "incorrect_esim",
    "sim_not_found",
    "multiple_sims_found",
]

const schema = yup.object({
    'sim_type': yup.string().required(),
    'msisdn': yup.string().matches(/^[0-9]{10}$/).required(),
    'personal_id': yup.string().matches(/^[0-9]{9}$/).required(),
    'four_cc_digits': yup.string().matches(/^[0-9]{4}$/).required(),
    'iccid': yup.string().when('sim_type', {
        is: (val) => val === 'sim',
        then: (schema) => schema.matches(/^[0-9]{7}$/).required(),
    }),
});

const apiError = ref(false);
const showOTPPopup = ref(false);
const otpCode = ref('');
const loader = ref(false);
const showErrorString = ref(false);
const outOfWorkingTimeModal = ref(false);
const fraudAccount = ref(false);

const disableFields = computed(() => showOTPPopup.value || apiError.value || loader.value);

const { values, handleSubmit, setFieldValue, meta } = useForm({
	validationSchema: schema,
    initialValues: {
        sim_type: 'sim',
    }
});


const maxLength = (event, number) => {
    if(event.target.value.length > number) {
        const slicedValue = event.target.value.slice(0, number);
        event.target.value = slicedValue;
        setFieldValue(event.target.name, slicedValue);
    }
};

const handleModalClose = () => {
	showOTPPopup.value = false;
	otpCode.value = '';
}

const onSubmit = handleSubmit(async (values) => {
    //values.four_cc_digits = 'test';
    loader.value = true;
    showErrorString.value = false;
    let response;
    try {
        response = await requestSimActivation(values);
    } catch (error) {
        apiError.value = true;
    }
    loader.value = false;

    if(!response) {
        return;
    }
    
    const objectResponse = JSON.parse(response.data);
    response = convertArrayToObject(response);

    if(!objectResponse.response.status && (objectResponse.response.error === 'required_otp' || objectResponse.response.error === 'requred_otp')) {
        showOTPPopup.value = true;
        return;
    }

    if(response.success && objectResponse?.response?.subscribers?.length) {
        props.setResponse({
            ...objectResponse.response,
            sim_type: values.sim_type,
        }, objectResponse.response.subscribers?.[0]?.is_ported);
        props.setPhone(values.msisdn);
    } else {
        // Convert the response to an object untill API is updated
        const errorResponse = convertArrayToObject(objectResponse.response);
        console.log(errorResponse)
        if(errorStrings.includes(errorResponse.error)) {
            showErrorString.value = true;
        } else if(errorResponse.error === "out_of_working_time") {
            outOfWorkingTimeModal.value = true;
        } else if(errorResponse?.errorMessage === "fraudulent-account") {
            fraudAccount.value = true;
        } else {
            apiError.value = true;
        }
    }
});

const onlyNumbers = (event) => {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
    setFieldValue(event.target.name, event.target.value);
    return event;
}

const numberValidation = (event) => {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
    let value = event.target.value;

    // Ensure the first two numbers are 05
    if (value.length >= 2 && value.substring(0, 2) !== '05') {
        value = '05' + value.substring(2);
    } else if (value.length < 2) {
        value = '05'.substring(0, value.length);
    }

    setFieldValue(event.target.name, value);
    return event;
}
</script>

<template>
    <div class="step-activation">
        <div class="step-activation__inner">
            <div class="step-activation__col step-activation__col--form">
                <h3>{{ __('Activate your new SIM!') }}</h3>
                <h4>{{ __('Choose the type of SIM you have') }}</h4>
                <form class="tab-form" @submit="onSubmit">
                    <div class="step-tabs">
                        <ul>
                            <li>
                                <Field :disabled="disableFields" type="radio" name="sim_type" id="sim_type1" value="sim" />
                                <label for="sim_type1">{{ __('SIM card') }}</label>
                            </li>
                            <li>
                                <Field :disabled="disableFields" type="radio" name="sim_type" id="sim_type2" value="esim" />
                                <label for="sim_type2">{{ __('eSIM') }}</label>
                            </li>
                        </ul>
                    </div>
                    <div class="field">
                        <label for="field-msisdn">{{ __('Type the phone number you want to activate') }} *</label>
                        <div class="field__input">
                            <Field :disabled="disableFields" type="text" id="field-msisdn" name="msisdn" @input="maxLength(numberValidation($event), 10)" required @paste="handleNumberFieldPaste" />
                            <!-- <Hint :help="true">
                                {{ __('A text with an explanation of this part, to be determined by the company and adjusted accordingly afterwards') }}
                            </Hint> -->
                        </div>
                    </div>
                    <div class="field">
                        <label for="field-personal_id">{{ __('ID number. entered when joining') }} *</label>
                        <div class="field__input">
                            <Field :disabled="disableFields" id="field-personal_id" name="personal_id" @input="maxLength(onlyNumbers($event), 9)" required />
                            <!-- <Hint :help="true">
                                {{ __('A text with an explanation of this part, to be determined by the company and adjusted accordingly afterwards') }}
                            </Hint> -->
                        </div>
                    </div>
                    <div class="field">
                        <label for="field-iccid">{{ __('Last 4 digits of the payment method') }} *</label>
                        <div class="field__input">
                            <Field :disabled="disableFields" name="four_cc_digits" id="field-four_cc_digits" @input="maxLength(onlyNumbers($event), 4)" required />
                            <!-- <Hint :help="true">
                                {{ __('A text with an explanation of this part, to be determined by the company and adjusted accordingly afterwards') }}
                            </Hint> -->
                        </div>
                    </div>
                    <div class="field" v-if="values.sim_type === 'sim'">
                        <label for="field-four_cc_digits">{{ __('Enter last 7 digits of SIM card') }} *</label>
                        <div class="field__input">
                            <p class="field__error" v-if="showErrorString">{{ __('SIM number is incorrect. must try again.') }}</p>
                            <Field :disabled="disableFields" type="text" name="iccid" id="field-iccid" @input="maxLength(onlyNumbers($event), 7)" required />
                            <Hint :help="true" class="tooltip--side" :hint="true">
                                <p><strong>{{ __('The SIM number on the card') }}</strong></p>
                                <p>{{ __('This number has 15 digits, and it appears in full on the physical card you received as described here:') }}</p>
                                <img :src="`${theme_path.url}/resources/images/sim-bg.png`" alt="">
                            </Hint> 
                        </div>
                    </div>
                    <div class="step-activation-cta">
                        <button :disabled="!meta.valid || loader" type="submit" class="btn btn--arrows">
                            {{ values.sim_type === 'sim' ? __('SIM activation') : __('Continued') }} <Loader v-if="loader && !showOTPPopup" />
                        </button>
                    </div>
                </form>
            </div>
            <div class="step-activation__col">
                <img v-if="values.sim_type === 'sim'" :src="`${theme_path.url}/resources/images/temp/activation-image.jpg`" />
                <img v-if="values.sim_type === 'esim'" :src="`${theme_path.url}/resources/images/temp/activation-image-esim.jpg`" />
            </div>
        </div>
    </div>
    
   <ModalOtp 
        :showOTPPopup="showOTPPopup" 
        :values="values"
        :handleModalClose="handleModalClose"
        :setResponse="setResponse"
        :setPhone="setPhone"
        @error="apiError = true"
   />
   <FraudModal class="popup--contact" :isActive="fraudAccount" @close="fraudAccount = false"></FraudModal>
    <Modal :isActive="apiError" @close="apiError = false">
        <template #body>
            <div class="popup-image">
                <img :src="`${theme_path.url}/resources/images/svg/icon-notice.svg`" />
            </div>
            <h4 class="fz26">{{ __('One or more of the details you entered do not exist!') }}</h4>
            <p class="mb24">
                {{ __('In order to activate the SIM card,') }}<br />
                {{ __('Please enter the correct details as they appeared in the purchase process') }}
            </p>
            <a href="#" class="btn w170" @click.prevent="apiError = false">אישור</a>
        </template>
    </Modal>
    <Modal :isActive="outOfWorkingTimeModal" @close="outOfWorkingTimeModal = false">
        <template #body>
            <div class="popup-image">
                <img :src="`${theme_path.url}/resources/images/svg/icon-notice.svg`" />
            </div>
            <h4 class="fz26">{{ __('The mobility system is now closed.') }}</h4>
            <p class="mb24">
                {{ __('Removals can be carried out Sunday-Thursday between 8:00 AM and 8:30 PM.') }}<br />
                {{ __('And on Fridays between 8:00 AM and 12:30 PM') }}
            </p>
            <a href="#" class="btn w170" @click.prevent="outOfWorkingTimeModal = false">{{ __("I don't want to move. I want to activate a new line.")}}</a>
        </template>
    </Modal>
</template>
